import { SelectOption, SelectGroup, OptionsOrGroups } from "@ir/ui";
import {
  DistributionCenter,
  Priority,
  Purchaser,
  Validity,
  AdjustmentType,
  AdjustmentCategories,
  DeductionTypeCategory,
  GLCode,
  BrandMember,
  Stage,
  Retailer,
  PromoType,
  PromoStatus,
  DisputeTrackingStatus,
} from "@/core/entities/lookup";

export const createSelectOption = (label: string, value: string, isDisabled?: boolean): SelectOption => {
  return { value, label, isDisabled };
};

export const buildPurchaserOptions = (purchasers: Purchaser[]): SelectOption[] => {
  return purchasers.map(purchaser => createSelectOption(purchaser.purchaserName, purchaser.purchaserId.toString()));
};

export const buildPromoTypeOptions = (promoTypes: PromoType[]): SelectOption[] => {
  return promoTypes.map(promoType => createSelectOption(promoType.promoTypeName, promoType.promoTypeId.toString()));
};

export const buildPromoStatusOptions = (promoStatuses: PromoStatus[]): SelectOption[] => {
  return promoStatuses.map(promoStatus =>
    createSelectOption(promoStatus.promoStatusName, promoStatus.promoStatusId.toString())
  );
};

export const buildDisputeTrackingStatusOptions = (
  statuses: DisputeTrackingStatus[]
): SelectOption[] => {
  return statuses.map(status =>
    createSelectOption(status.disputeTrackingStatusName, status.disputeTrackingStatusId.toString())
  );
};

export const buildPriorityOptions = (priorities: Priority[]): SelectOption[] => {
  return priorities.map(priority => createSelectOption(priority.label, priority.value.toString()));
};

export const buildValidityOptions = (validities: Validity[]): SelectOption[] => {
  return validities.map(validity => createSelectOption(validity.label, validity.value.toString()));
};

export const buildAdjustmentCategoryOptions = (categories: AdjustmentCategories[]): SelectOption[] => {
  return categories.map(category => ({
    value: category.id.toString(),
    label: category.categoryName,
  }));
};

export const buildDeductionTypeCategoryOptions = (categories: DeductionTypeCategory[]): SelectOption[] => {
  return categories.map(category =>
    createSelectOption(category.deductionTypeCategoryName, category.deductionTypeCategoryId.toString())
  );
};

const hasAllCategories = (selectedCategoryIds: string[] | undefined): boolean => {
  if (selectedCategoryIds?.length !== 3) {
    return false;
  }
  return selectedCategoryIds.includes("1") && selectedCategoryIds.includes("2") && selectedCategoryIds.includes("3");
};

const hasCategories = (selectedCategoryIds: string[] | undefined, ...categoryIds: string[]): boolean => {
  if (!selectedCategoryIds) {
    return false;
  }
  return categoryIds.every(id => selectedCategoryIds.includes(id));
};

const shouldIncludeSubType = (subTypeKey: string, selectedCategoryIds: string[] | undefined): boolean => {
  if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
    return true;
  }

  // If all three categories (1, 2, 3) are selected, include all subtypes
  if (hasAllCategories(selectedCategoryIds)) {
    return true;
  }

  // Categories 3 + 2: DEDUCTION_PRE_PAYMENT included, CREDIT_PRE_PAYMENT excluded
  if (hasCategories(selectedCategoryIds, "2", "3") && !selectedCategoryIds.includes("1")) {
    return subTypeKey !== "CREDIT_PRE_PAYMENT";
  }

  // Categories 3 + 1: CREDIT_PRE_PAYMENT included, DEDUCTION_PRE_PAYMENT excluded
  if (hasCategories(selectedCategoryIds, "1", "3") && !selectedCategoryIds.includes("2")) {
    return subTypeKey !== "DEDUCTION_PRE_PAYMENT";
  }

  // Single category selections
  if (selectedCategoryIds.length === 1) {
    const categoryId = selectedCategoryIds[0];
    if (categoryId === "1") {
      return subTypeKey === "CREDIT_PRE_PAYMENT";
    }
    if (categoryId === "2") {
      return subTypeKey !== "CREDIT_PRE_PAYMENT";
    }
    if (categoryId === "3") {
      return subTypeKey !== "CREDIT_PRE_PAYMENT" && subTypeKey !== "DEDUCTION_PRE_PAYMENT";
    }
  }

  // Default: include all if no specific rule matches
  return true;
};

const filterTypesByCategory = (
  types: AdjustmentType[],
  selectedCategoryIds: string[] | undefined
): AdjustmentType[] => {
  if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
    return types;
  }

  return types.filter(adjustmentType =>
    adjustmentType.subTypes?.some(subType => shouldIncludeSubType(subType.subTypeKey, selectedCategoryIds))
  );
};

const filterSubTypesByCategory = (
  subTypes: NonNullable<AdjustmentType["subTypes"]>,
  selectedCategoryIds: string[] | undefined
): NonNullable<AdjustmentType["subTypes"]> => {
  if (!selectedCategoryIds || selectedCategoryIds.length === 0) {
    return subTypes;
  }

  return subTypes.filter(subType => shouldIncludeSubType(subType.subTypeKey, selectedCategoryIds));
};

const filterSubTypesByDeductionTypeCategory = (
  subTypes: NonNullable<AdjustmentType["subTypes"]>,
  selectedDeductionTypeCategoryIds: string[] | undefined
): NonNullable<AdjustmentType["subTypes"]> => {
  if (!selectedDeductionTypeCategoryIds || selectedDeductionTypeCategoryIds.length === 0) {
    return subTypes;
  }

  return subTypes.filter(subType =>
    selectedDeductionTypeCategoryIds.includes(
      subType.deductionTypeCategory?.deductionTypeCategoryId.toString() ?? ""
    )
  );
};

export const buildAdjustmentTypeOptions = (
  types: AdjustmentType[],
  selectedCategoryIds: string[] | undefined,
  createOption: (label: string, value: string) => SelectOption,
  selectedDeductionTypeCategoryIds?: string[] | undefined
): OptionsOrGroups<SelectOption, SelectGroup> => {
  const filteredTypes = filterTypesByCategory(types, selectedCategoryIds);
  const result: Array<SelectOption | SelectGroup> = [];

  for (const adjustmentType of filteredTypes) {
    if (adjustmentType.subTypes) {
      const filteredByAdjustmentCategory = filterSubTypesByCategory(
        adjustmentType.subTypes,
        selectedCategoryIds
      );
      const filteredSubTypes = filterSubTypesByDeductionTypeCategory(
        filteredByAdjustmentCategory,
        selectedDeductionTypeCategoryIds
      );

      if (filteredSubTypes.length > 0) {
        result.push({
          value: adjustmentType.id.toString(),
          label: adjustmentType.typeName,
          options: filteredSubTypes.map(subType => createOption(subType.subTypeName, subType.id.toString())),
        });
      }
    } else if (!selectedDeductionTypeCategoryIds || selectedDeductionTypeCategoryIds.length === 0) {
      // Types without subtypes cannot match a deduction type category; only include when that filter is idle
      result.push({
        value: adjustmentType.id.toString(),
        label: adjustmentType.typeName,
      });
    }
  }

  return result as OptionsOrGroups<SelectOption, SelectGroup>;
};

/** Collect flat subtype option values currently allowed by both category filters. */
export const getAllowedAdjustmentTypeValues = (
  types: AdjustmentType[],
  selectedCategoryIds: string[] | undefined,
  selectedDeductionTypeCategoryIds?: string[] | undefined
): Set<string> => {
  const options = buildAdjustmentTypeOptions(
    types,
    selectedCategoryIds,
    createSelectOption,
    selectedDeductionTypeCategoryIds
  );
  const values = new Set<string>();

  for (const option of options) {
    if ("options" in option && Array.isArray(option.options)) {
      for (const child of option.options) {
        values.add(child.value);
      }
    } else if ("value" in option && option.value != null) {
      values.add(option.value);
    }
  }

  return values;
};

export const buildDistributionCenterOptions = (
  distributionCenters: DistributionCenter[],
  selectedPurchaserIds: string[] | undefined
): SelectOption[] => {
  const noneDistributionCenter = new DistributionCenter({
    dcId: 0,
    dcCode: "",
    dcName: "none",
    purchaserId: 0,
    purchaserName: "",
  });

  let filteredCenters = distributionCenters;

  // Filter by selected purchasers if any
  if (selectedPurchaserIds && selectedPurchaserIds.length > 0) {
    filteredCenters = distributionCenters.filter(dc => selectedPurchaserIds.includes(dc.purchaserId.toString()));
  }

  const options = [noneDistributionCenter, ...filteredCenters].map(dc => {
    if (dc.dcId === 0) {
      return createSelectOption("None", `${dc.dcId}`);
    }
    return createSelectOption(`${dc.purchaserName} - ${dc.dcName} (${dc.dcCode})`, dc.dcId.toString());
  });

  return options;
};

export const buildRetailerOptions = (retailers: Retailer[]): SelectOption[] => {
  return retailers.map(retailer =>
    createSelectOption(retailer.retailerName ?? "", retailer.retailerId?.toString() ?? "")
  );
};

export const buildGLCodeOptions = (glCodes: GLCode[]): SelectOption[] => {
  return glCodes.map(glCode => createSelectOption(glCode.displayName, glCode.glCodeId.toString()));
};

export const buildBrandMembersOptions = (brandMembers: BrandMember[]): SelectOption[] => {
  const brandMembersSetOptions = new Set<SelectOption>(
    brandMembers.map(brandMember => {
      const label =
        brandMember.roles.length > 0
          ? `${brandMember.fullName} (${brandMember.roles?.map(role => role.roleName).join(", ")})`
          : brandMember.fullName;
      return createSelectOption(label, brandMember.userId.toString());
    })
  );

  return [
    {
      value: "0",
      label: "Unassigned",
    },
    ...brandMembersSetOptions,
  ];
};

export const buildProgressOptions = (progress: Stage[]): OptionsOrGroups<SelectOption, SelectGroup> => {
  const result: Array<SelectOption | SelectGroup> = [];
  const stages = progress.filter(stage => stage.parentId === null);
  const statuses = progress.filter(stage => stage.parentId !== null);

  for (const stage of stages) {
    result.push({
      label: stage.stageName,
      value: stage.id?.toString(),
      options: statuses
        .filter(status => status.parentId === stage.id)
        .map(status => createSelectOption(status.stageName, status.id?.toString())),
    });
  }
  return result as OptionsOrGroups<SelectOption, SelectGroup>;
};
